/**
 * @file Cache de geometries primitives PARTAGEES par cle (sphere, capsule, cylindre, cone, boite arrondie...) : une seule allocation GPU par forme.
 */
import { BoxGeometry, CapsuleGeometry, ConeGeometry, CylinderGeometry, IcosahedronGeometry, SphereGeometry, TorusGeometry } from 'three';
import type { BufferGeometry } from 'three';
import type { ResourceScope, ResourceTracker } from './ResourceTracker';

export class GeometryLibrary {
  private readonly cache = new Map<string, BufferGeometry>();

  constructor(private readonly res: ResourceTracker) {}

  /** Geometrie mise en cache sous `key`, creee par `make` au premier appel. */
  get(key: string, make: () => BufferGeometry, scope: ResourceScope = 'global'): BufferGeometry {
    let g = this.cache.get(key);
    if (!g) {
      g = this.res.track(make(), scope);
      this.cache.set(key, g);
    }
    return g;
  }

  /** Oublie les entrees d'une portee liberee (appele apres ResourceTracker.release('biome'|'mode')). */
  forgetPrefix(prefix: string): void {
    for (const k of [...this.cache.keys()]) if (k.startsWith(prefix)) this.cache.delete(k);
  }

  sphere(r = 1, w = 16, h = 12): BufferGeometry {
    return this.get(`sphere:${r}:${w}:${h}`, () => new SphereGeometry(r, w, h));
  }
  capsule(r: number, len: number, cap = 6, radial = 12): BufferGeometry {
    return this.get(`capsule:${r}:${len}:${cap}:${radial}`, () => new CapsuleGeometry(r, len, cap, radial));
  }
  cylinder(rt: number, rb: number, h: number, radial = 12): BufferGeometry {
    return this.get(`cyl:${rt}:${rb}:${h}:${radial}`, () => new CylinderGeometry(rt, rb, h, radial));
  }
  cone(r: number, h: number, radial = 12): BufferGeometry {
    return this.get(`cone:${r}:${h}:${radial}`, () => new ConeGeometry(r, h, radial));
  }
  box(w: number, h: number, d: number): BufferGeometry {
    return this.get(`box:${w}:${h}:${d}`, () => new BoxGeometry(w, h, d));
  }
  ico(r: number, detail = 1): BufferGeometry {
    return this.get(`ico:${r}:${detail}`, () => new IcosahedronGeometry(r, detail));
  }
  torus(r: number, tube: number): BufferGeometry {
    return this.get(`torus:${r}:${tube}`, () => new TorusGeometry(r, tube, 8, 20));
  }
}
